import {
  Button,
  cx,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Dialogß,
} from '@otter-ai/den-components';
import { CheckmarkIcon } from '@otter-ai/den-icons';
import type * as recall from '@otter-ai/recall-client';
import {
  GalleryTemplatePaywallImpressionEvent,
  GeneralButtonActionEvent,
  NavigateUseTemplateGalleryEvent,
  type Screen,
  trackEvent,
  type UpgradePlanType,
} from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useUpgradeDetails } from '@/shared/hooks/useUpgradeDetails';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { useSubscription } from '../../shared/hooks/useSubscription';
import { initialTemplateAtom } from '../atoms/templateAtom';
import { galleryTemplatePreviewsQueryCreator } from '../query/galleryTemplatePreviewsQueryCreator';
import { galleryTemplateQueryCreator } from '../query/galleryTemplateQueryCreator';
import {
  createUrl,
  getGradientClass,
  getIcon,
  getTitle,
  redirectUrl,
} from '../utils/template.utils';
import { PaywallTooltip } from './PaywallTooltip';
import { TemplateGalleryBuilderBlock } from './TemplateGalleryBuilderBlock';

type TemplateGalleryProps = {
  onClose: () => void;
  type: 'personal' | 'admin';
  url?: string;
  screen?: Screen;
  defaultSelectedType?: string;
};

export function PaywallTemplateGallery({
  onClose,
  type,
  url,
  screen,
  defaultSelectedType,
}: TemplateGalleryProps) {
  const [selected, setSelected] = useState<recall.GalleryTemplatePreview>();
  const setInitialTemplate = useSetAtom(initialTemplateAtom);
  const router = useRouter();
  const {
    upgradeHref,
    upgradeButtonText,
    userCanUpgrade,
    isWorkspaceAdmin,
    upgradePlan,
  } = useUpgradeDetails();

  const { subscription } = useSubscription();

  // Fetch gallery templates from the API.
  const { data: galleryTemplates, isLoading: isLoadingGalleryTemplates } =
    useQuery(galleryTemplatePreviewsQueryCreator());

  const { workspace } = useWorkspace();

  const lockedTemplates = galleryTemplates?.filter((t) => t.is_locked);
  const unlockedTemplates = galleryTemplates?.filter((t) => !t.is_locked);

  // When galleryTemplates are loaded, set the first one as the default selected template if a default was not passed in
  useEffect(() => {
    if (galleryTemplates && galleryTemplates.length > 0 && !selected) {
      const initialSelected = defaultSelectedType
        ? galleryTemplates.find(
            (t) => t.base_template_type === defaultSelectedType,
          )
        : galleryTemplates[0];
      setSelected(initialSelected);
    }
  }, [galleryTemplates, selected, defaultSelectedType]);

  useEffect(() => {
    if (selected?.is_locked) {
      trackEvent(
        GalleryTemplatePaywallImpressionEvent({
          IsAdmin: isWorkspaceAdmin,
          UpgradePlanType: upgradePlan as UpgradePlanType,
          Screen: screen,
          LockedTemplatesShown: lockedTemplates && lockedTemplates?.length > 0,
          UpgradeText: upgradeButtonText,
        }),
      );
    }
  }, [
    selected,
    isWorkspaceAdmin,
    upgradePlan,
    screen,
    lockedTemplates,
    upgradeButtonText,
  ]);

  // Fetch detailed template data based on the selected template.
  const { data: templateDetails, isLoading: isLoadingDetails } = useQuery(
    galleryTemplateQueryCreator(selected?.base_template_type),
  );

  console.log('url', url);
  const templateRedirect = url || redirectUrl(type);
  console.log('templateRedirect', templateRedirect);
  const newTemplateUrl = createUrl(templateRedirect);
  console.log('newTemplateUrl', newTemplateUrl);

  const handleUseTemplate = () => {
    if (!templateDetails || !selected) return;
    const selectedTemplateName = selected.name === 'Blank' ? '' : selected.name;
    const payload = {
      ...templateDetails,
      name: selectedTemplateName,
      is_personal_template: type === 'personal',
      base_template_type: selected.base_template_type,
      is_customized: false,
      blocks: templateDetails.blocks ?? [],
    };
    setInitialTemplate(payload);
    trackEvent(
      NavigateUseTemplateGalleryEvent({
        TemplateName: selectedTemplateName,
        TemplateType: type,
      }),
    );
    router.push(newTemplateUrl.toString());
  };

  const selectedRef = useRef<HTMLDivElement | null>(null);

  // if the default selected template is not in view, scroll to it
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isLoadingGalleryTemplates]);

  return (
    <Dialogß
      size="lg"
      Header={
        <DialogHeader title="Templates" className="border-b border-subtle" />
      }
      data-testid="template-gallery"
      onOpenChange={onClose}
    >
      <DialogContent
        data-testid="template-gallery-content"
        className="border-b border-subtle p-0"
      >
        <div className="flex h-[500px]">
          {/* Left Panel: Template List */}
          <div
            data-testid="template-list"
            className="w-1/3 border-r border-subtle overflow-y-auto p-2"
          >
            <div className="px-2 py-1">
              {/* Unlocked Templates */}
              {unlockedTemplates?.length ? (
                <>
                  <div className="font-semibold text-muted text-xs uppercase px-2 py-1">
                    Available Templates
                  </div>
                  <ul className="mb-2">
                    {unlockedTemplates.map((template) => (
                      <div
                        data-testid={`unlocked-template-item-${template.name}`}
                        key={template.id}
                        className="flex items-center cursor-pointer rounded-md p-3 font-semibold text-sm hover:bg-default-hover justify-between"
                        onClick={() => setSelected(template)}
                        onKeyUp={() => {}}
                      >
                        {template.name}
                        {selected?.id === template.id && (
                          <CheckmarkIcon size={20} />
                        )}
                      </div>
                    ))}
                  </ul>
                </>
              ) : null}

              {/* Locked Templates */}
              {lockedTemplates?.length ? (
                <>
                  <div className="font-semibold text-muted text-xs uppercase px-2 py-1">
                    Upgrade for more
                  </div>
                  <ul>
                    {lockedTemplates.map((template) => (
                      <div
                        data-testid={`locked-template-item-${template.name}`}
                        key={template.id}
                        className="flex items-center cursor-pointer rounded-md p-3 font-semibold text-sm hover:bg-default-hover justify-between"
                        onClick={() => setSelected(template)}
                        onKeyUp={() => {}}
                        ref={
                          template.base_template_type === defaultSelectedType
                            ? selectedRef
                            : null
                        }
                      >
                        <span className="flex items-center">
                          {template.name}
                          <PaywallTooltip />
                        </span>
                        {selected?.id === template.id && (
                          <CheckmarkIcon size={20} />
                        )}
                      </div>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          </div>

          {/* Right Panel: Content View */}
          <div
            data-testid="template-preview"
            className={cx(
              'flex-1 h-full overflow-y-auto',
              getGradientClass(selected?.id),
            )}
          >
            <div className="h-full p-8">
              <div className="rounded-md bg-default shadow-md p-6 flex-col gap-6">
                <span className="text-2xl font-semibold">{selected?.name}</span>
                <div className="mt-6">
                  {selected?.blocks.map((block: recall.BlockPreview) => (
                    <TemplateGalleryBuilderBlock
                      key={block.block_type}
                      icon={getIcon(block.block_type)}
                      title={getTitle(block.block_type)}
                      content={block.block_content ?? null}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogFooter
        data-testid="template-gallery-footer"
        className={cx(
          'flex items-center gap-2',
          selected?.is_locked ? 'justify-between' : 'justify-end',
        )}
      >
        {selected?.is_locked ? (
          <>
            <span className="text-sm text-subtle">
              This template isn’t included in your plan. Upgrade to unlock
              access.
            </span>
            {userCanUpgrade && !workspace?.settings?.sales_assisted && (
              <Button asChild>
                <a
                  // TODO: replace with <Link> when upgradeHref is within Next.js
                  href={upgradeHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="upgrade-template-button"
                  onClick={() => {
                    trackEvent(
                      GeneralButtonActionEvent({
                        TemplateId: selected.id,
                        TemplateName: selected.name,
                        IsAdmin: isWorkspaceAdmin,
                        UpgradePlanType: upgradePlan as UpgradePlanType,
                        Screen: screen,
                        LockedTemplatesShown:
                          lockedTemplates && lockedTemplates?.length > 0,
                        UpgradeText: upgradeButtonText,
                      }),
                    );
                  }}
                >
                  {upgradeButtonText}
                </a>
              </Button>
            )}
          </>
        ) : (
          <Button
            disabled={
              isLoadingDetails ||
              !templateDetails ||
              subscription?.meeting_types?.at_limit
            }
            onClick={handleUseTemplate}
            data-testid="use-template-button"
          >
            Use Template
          </Button>
        )}
      </DialogFooter>
    </Dialogß>
  );
}
